import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Col, Form, Row } from 'reactstrap';
import TextField from '../../../../../common/TextField';
import MobileField from '../../../../../common/MobileField';
import Dropdown from '../../../../../common/DropDown';
import DatePicker from '../../../../../common/DatePicker';
import { Continue } from '../../../../../../Utils/Constants';
import { H3, H4, H5 } from '../../../../../../AbstractElements';
import { Client1Errors, Client1FormData } from '../../../FormData';
import DollarInput from '../../../../../common/DollarInput';
import { useMutationRequestQuery } from '../../../../../../Hooks/useMutationRequestQuery';
import { insertUpdateClientService } from '../../../../../../Services/PublicPageServices';
import { setFormStatus } from '../../../../../../ReduxToolkit/Reducers/FromStatusSlice';
import { useDispatch } from 'react-redux';
import { setFormsData } from '../../../../../../ReduxToolkit/Reducers/FormDataSlice';
import { updateFormData } from '../../../../../../ReduxToolkit/Reducers/FactFindDataSlice';

export default function Client1Form({ activeCallBack, clientFactFindData, clientId, setClientId, setClient1formData }: any) {
    const [formData, setFormData] = useState<any>(Client1FormData);
    const [formErrors, setFormErrors] = useState(Client1Errors);
    const validateFirstName = (value: string) => value.length >= 3;
    const validateLastName = (value: string) => value.length >= 3;
    const validateMobile = (value: string) => value !== "";
    const validateEmail = (value: string) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);
    const validateGender = (value: string) => value !== "";
    const validateDob = (value: Date | null) => value !== null;
    const validateDropdown = (value: string) => value !== "";
    const validateTextField = (value: string) => value.trim() !== "";
    const validateDollarInput = (value: number | null) => value !== null;
    const validateDate = (value: Date | null) => value !== null;
    const dispatch = useDispatch();


    const { mutate: insertUpdateClient, isPending: isInsertUpdateClientPending } = useMutationRequestQuery(
        'insertUpdateClientService',
        insertUpdateClientService,
        {
            onSuccess: (data: any) => {
                toast.success("Client information submitted successfully.");
                setClientId(data?.data?.generatedClientID)
                dispatch(setFormStatus({ id: '1', status: 1 }));
                // setFormErrors(Client1Errors)
                activeCallBack(2);
            },
            onError: (error: any) => {
                toast.error("Failed to submit client information.");
                console.error(error);
            },
        }
    );

    const formatClientData = (formData: any) => {
        return {
            clientID: clientId,
            clientSurnName: formData.firstName,
            dateUpdated: "2025-01-14T04:56:17.173Z",
            client1FirstName: formData.firstName,
            client1LastName: formData.lastName,
            client1Email: formData.email,
            client1Mobile: formData.mobile,
            client1DateOfBirth: formData.dob,
            client1Gender: formData.gender,
            client1ResidentStatus: formData.residentStatus,
            client1MaritalStatus: formData.maritalStatus,
            client1CreditStatus: formData.creditStatus,
            client1HomeStatus: formData.homeStatus,

            // Address history (3 years)
            client1Address1: formData.address1,
            client1Address2: formData.address2,
            client1Address3: formData.address3,
            client1Address1StartDate: formData.startDate1,
            client1Address2StartDate: formData.startDate2,
            client1Address3StartDate: formData.startDate3,

            // Income details
            client1EmploymentSalaryWagesDLL: formData.salaryWages,
            client1IncomeSalaryWages: formData.salaryWagesIncome,
            client1IncomeSalaryFrequency: formData.salaryWagesFrequency,
            client1FamilyAllowance: formData.familyAllowance,
            client1FamilyAllowanceFrequency: formData.familyAllowanceFrequency,
            client1DividendIncome: formData.dividendIncome,
            client1DividendIncomeFrequency: formData.dividendIncomeFrequency,
            client1CompanyNetProfit: formData.companyNetProfit,
            client1CompanyNetProfitFrequency: formData.companyNetProfitFrequency,

            // Employment history (3 records)
            client1CompanyName1: formData.employment1CompanyName,
            client1Role1: formData.employment1Role,
            client1StartDate1: formData.employment1StartDate,
            client1CompanyName2: formData.employment2CompanyName,
            client1Role2: formData.employment2Role,
            client1StartDate2: formData.employment2StartDate,
            client1CompanyName3: formData.employment3CompanyName,
            client1Role3: formData.employment3Role,
            client1StartDate3: formData.employment3StartDate,
        };
    };

    useEffect(() => {
        setClient1formData(formData)
        dispatch(setFormsData({
            id: '1',
            formData: formData,
        }));
    }, [formData])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // Validate mandatory fields
        const firstNameValid = validateFirstName(formData.firstName);
        const lastNameValid = validateLastName(formData.lastName);
        const mobileValid = validateMobile(formData.mobile);
        const emailValid = validateEmail(formData.email);
        const dobValid = validateDob(formData.dob);
        const genderValid = validateGender(formData.gender);
        const maritalStatusValid = validateDropdown(formData.maritalStatus);
        const residentStatusValid = validateDropdown(formData.residentStatus);
        const creditStatusValid = validateDropdown(formData.creditStatus);
        const homeStatusValid = validateDropdown(formData.homeStatus);

        // Validate Address and Employment Details (only for non-empty fields)
        const address1Valid = validateTextField(formData.address1);
        const startDate1Valid = validateDate(formData.startDate1);

        // Optional fields check - only validate if not empty or null
        const address2Valid = formData.address2 ? validateTextField(formData.address2) : true;
        const startDate2Valid = formData.startDate2 ? validateDate(formData.startDate2) : true;

        const address3Valid = formData.address3 ? validateTextField(formData.address3) : true;
        const startDate3Valid = formData.startDate3 ? validateDate(formData.startDate3) : true;

        // Optional Employment Fields validation (only if fields are not empty)
        const employment1CompanyNameValid = formData.employment1CompanyName ? validateTextField(formData.employment1CompanyName) : true;
        const employment1RoleValid = formData.employment1Role ? validateTextField(formData.employment1Role) : true;
        const employment1StartDateValid = formData.employment1StartDate ? validateDate(formData.employment1StartDate) : true;

        const employment2CompanyNameValid = formData.employment2CompanyName ? validateTextField(formData.employment2CompanyName) : true;
        const employment2RoleValid = formData.employment2Role ? validateTextField(formData.employment2Role) : true;
        const employment2StartDateValid = formData.employment2StartDate ? validateDate(formData.employment2StartDate) : true;

        const employment3CompanyNameValid = formData.employment3CompanyName ? validateTextField(formData.employment3CompanyName) : true;
        const employment3RoleValid = formData.employment3Role ? validateTextField(formData.employment3Role) : true;
        const employment3StartDateValid = formData.employment3StartDate ? validateDate(formData.employment3StartDate) : true;

        // Validate Income Fields
        const salaryWagesValid = validateDropdown(formData.salaryWages);
        const salaryWagesIncomeValid = validateDollarInput(formData.salaryWagesIncome);

        // Validate frequency only if corresponding income is not "0"
        const salaryWagesFrequencyValid = formData.salaryWagesIncome !== '0' ? validateDropdown(formData.salaryWagesFrequency) : true;

        const familyAllowanceValid = validateDollarInput(formData.familyAllowance);
        const familyAllowanceFrequencyValid = formData.familyAllowance !== '0' ? validateDropdown(formData.familyAllowanceFrequency) : true;

        const dividendIncomeValid = validateDollarInput(formData.dividendIncome);
        const dividendIncomeFrequencyValid = formData.dividendIncome !== '0' ? validateDropdown(formData.dividendIncomeFrequency) : true;

        const companyNetProfitValid = validateDollarInput(formData.companyNetProfit);
        const companyNetProfitFrequencyValid = formData.companyNetProfit !== '0' ? validateDropdown(formData.companyNetProfitFrequency) : true;

        const isValid =
            firstNameValid &&
            lastNameValid &&
            mobileValid &&
            emailValid

        // If any validation fails, set errors for each field
        setFormErrors({
            firstName: !firstNameValid,
            lastName: !lastNameValid,
            mobile: !mobileValid,
            email: !emailValid,
            dob: !dobValid,
            gender: !genderValid,
            maritalStatus: !maritalStatusValid,
            residentStatus: !residentStatusValid,
            creditStatus: !creditStatusValid,
            homeStatus: !homeStatusValid,
            address1: !address1Valid,
            startDate1: !startDate1Valid,
            address2: !address2Valid,
            startDate2: !startDate2Valid,
            address3: !address3Valid,
            startDate3: !startDate3Valid,
            salaryWages: !salaryWagesValid,
            salaryWagesIncome: !salaryWagesIncomeValid,
            salaryWagesFrequency: !salaryWagesFrequencyValid,
            familyAllowance: !familyAllowanceValid,
            familyAllowanceFrequency: !familyAllowanceFrequencyValid,
            dividendIncome: !dividendIncomeValid,
            dividendIncomeFrequency: !dividendIncomeFrequencyValid,
            companyNetProfit: !companyNetProfitValid,
            companyNetProfitFrequency: !companyNetProfitFrequencyValid,
            employment1CompanyName: !employment1CompanyNameValid,
            employment1Role: !employment1RoleValid,
            employment1StartDate: !employment1StartDateValid,
            employment2CompanyName: !employment2CompanyNameValid,
            employment2Role: !employment2RoleValid,
            employment2StartDate: !employment2StartDateValid,
            employment3CompanyName: !employment3CompanyNameValid,
            employment3Role: !employment3RoleValid,
            employment3StartDate: !employment3StartDateValid,
        });
        if (isValid) {
            const formattedData = formatClientData(formData);
            dispatch(
                updateFormData({
                    formName: "client1Details",
                    formData: formattedData,
                })
            );
            insertUpdateClient(formattedData);
        }

    };

    const handleValueChange = (name: string, value: string | Date | null) => {
        setFormData((prevData: any) => ({
            ...prevData,
            [name]: value,
        }));
    };
    // console.log(formData);
    const handleErrorChange = (name: string, error: boolean) => {
        setFormErrors((prevErrors) => ({
            ...prevErrors,
            [name]: error,
        }));
    };

    const splitDropdownValues = (dropdownValues: string) => {
        return dropdownValues ? dropdownValues.split(',').map(value => value.trim()) : [];
    };

    return (
        <Form onSubmit={handleSubmit} style={{
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            padding: '30px',
            backgroundColor: '#fff',
        }}>
            <Row className='g-3'>
            <Col xs={12} className='mb-4'>
                    <Row className='align-items-center'>
                        <Col className='text-left'>
                            <H3 className='f-w-900 heading-color m-0'>Client 1 - Details</H3>
                        </Col>
                        <Col className='text-end'>
                            <Button 
                                disabled={isInsertUpdateClientPending}
                                type="submit" 
                                color="success" 
                                style={{ fontSize: '18px' }}
                            >
                                {isInsertUpdateClientPending ? "Loading..." : Continue}
                            </Button>
                        </Col>
                    </Row>
                </Col>
           
                <Row className="gx-2">
                    {/* First Name Field */}
                    <Col sm={10} className='sm-row' >
                        <TextField
                            label="First Name"
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            setValue={handleValueChange}
                            placeholder="Enter your first name"
                            // required={true}
                            setError={handleErrorChange}
                            errorMessage="First Name is required"
                            error={formErrors.firstName}
                            design='flex'
                            passCol='col-5'
                            tabIndex={1}
                            maxChars={30}
                        />
                    </Col>

                    {/* Last Name Field */}
                    <Col sm={10} className='sm-row' >
                        <TextField
                            label="Last Name"
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            setValue={handleValueChange}
                            placeholder="Enter your last name"
                            setError={handleErrorChange}
                            errorMessage="Last Name is required"
                            error={formErrors.lastName}
                            // required={true}
                            design='flex'
                            passCol='col-5'
                            maxChars={30}
                            tabIndex={2}
                        />
                    </Col>

                    {/* Mobile Field */}
                    <Col sm={10} className='sm-row' >
                        <MobileField
                            label="Mobile Number"
                            mobileField="mobile"
                            value={formData.mobile}
                            setValue={(field, value) => handleValueChange(field, value)}
                            placeholder="0000 000 000"
                            errorMessage="Invalid mobile number"
                            error={formErrors.mobile}
                            // required
                            design="flex"
                            passCol="col-3"
                            maxLength={18} // 12 digits for 0000 0000 0000 format
                            setError={handleErrorChange}

                            tabIndex={3}
                        />
                    </Col>

                    {/* Email Field */}
                    <Col sm={10} className='sm-row' >
                        <TextField
                            label="Email"
                            type="text"
                            name="email"
                            value={formData.email}
                            setValue={handleValueChange}
                            setError={handleErrorChange}
                            errorMessage="Email is required"
                            error={formErrors.email}
                            placeholder="Enter your email"
                            // required={true}
                            design='flex'
                            maxChars={40}
                            passCol='col-6'
                            tabIndex={4}
                        />
                    </Col>

                    {/* Gender Dropdown */}
                    <Col sm={10} className='sm-row' >
                        {/* <Col md={7}> */}
                        <Dropdown
                            label="Gender"
                            name="gender"
                            value={formData.gender}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[0].dropDownValues : '')}
                            // required={true}
                            // setError={handleErrorChange}
                            // error={formErrors.gender}
                            // errorMessage="Please select a gender"
                            design='flex'
                            passCol='col-3'
                            tabIndex={5}
                        />
                        {/* </Col> */}
                    </Col>

                    {/* Date of Birth DatePicker */}
                    <Col sm={10} className='sm-row' >
                        {/* <Col md={7}> */}
                        <DatePicker
                            label="Date of Birth"
                            name="dob"
                            value={formData.dob}
                            setValue={handleValueChange}
                            // required={true}
                            // setError={handleErrorChange}
                            // error={formErrors.dob}
                            // errorMessage="Please select DOB"
                            design='flex'
                            passCol='col-3'
                            tabIndex={6}
                        />
                        {/* </Col> */}
                    </Col>

                    {/* Marital Status Dropdown */}
                    <Col sm={10} className='sm-row' >
                        {/* <Col md={7}> */}
                        <Dropdown
                            label="Marital Status"
                            name="maritalStatus"
                            value={formData.maritalStatus}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[1].dropDownValues : '')}
                            // required={true}
                            // setError={handleErrorChange}
                            // error={formErrors.maritalStatus}
                            // errorMessage="Please select marital status"
                            design='flex'
                            passCol='col-3'
                            tabIndex={7}
                        />
                        {/* </Col> */}
                    </Col>

                    {/* Resident Status Dropdown */}
                    <Col sm={10} className='sm-row' >
                        {/* <Col md={7}> */}
                        <Dropdown
                            label="Resident Status"
                            name="residentStatus"
                            value={formData.residentStatus}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[2].dropDownValues : '')}
                            // required={true}
                            // setError={handleErrorChange}
                            // error={formErrors.residentStatus}
                            // errorMessage="Please select a resident status"
                            design='flex'
                            passCol='col-3'
                            tabIndex={8}
                        />
                        {/* </Col> */}
                    </Col>

                    {/* Credit Status Dropdown */}
                    <Col sm={10} className='sm-row' >
                        {/* <Col md={7}> */}
                        <Dropdown
                            label="Credit Status"
                            name="creditStatus"
                            value={formData.creditStatus}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[3].dropDownValues : '')}
                            // required={true}
                            // setError={handleErrorChange}
                            // error={formErrors.creditStatus}
                            // errorMessage="Please select a credit status"
                            design='flex'
                            passCol='col-3'
                            tabIndex={9}
                        />
                        {/* </Col> */}
                    </Col>

                    {/* Home Status Dropdown */}
                    <Col sm={10} className='sm-row' >
                        {/* <Col md={7}> */}
                        <Dropdown
                            label="Home Status"
                            name="homeStatus"
                            value={formData.homeStatus}
                            setValue={handleValueChange}
                            options={splitDropdownValues(clientFactFindData ? clientFactFindData[8].dropDownValues : '')}
                            // required={true}
                            // setError={handleErrorChange}
                            // error={formErrors.homeStatus}
                            // errorMessage="Please select a home status"
                            design='flex'
                            passCol='col-3'
                            tabIndex={10}
                        />
                        {/* </Col> */}
                    </Col>
                    <Col xs={12} className="my-4">
                        {/* Address History Section */}
                        <H3 className="f-w-900 heading-color mt-2 text-left">Address History</H3>
                        <Row className="gx-2 my-4">
                            {/* First Column: Home Address */}
                            <Col xs={12} md={7}>
                                <H4 className="heading-color f-w-700 mb-3 text-left">Home Address - we need 3 years history</H4>
                                <TextField
                                    type="text"
                                    name="address1"
                                    enableAddressFinder={true}
                                    value={formData.address1}
                                    setValue={handleValueChange}
                                    placeholder="Enter address 1"
                                    required
                                    // error={formErrors.address1}
                                    // errorMessage="Address is required"
                                    // setError={handleErrorChange}
                                    maxChars={60}
                                    tabIndex={11}
                                />
                                <TextField
                                    type="text"
                                    name="address2"
                                    enableAddressFinder={true}
                                    value={formData.address2}
                                    setValue={handleValueChange}
                                    placeholder="Enter address 2"
                                    required
                                    maxChars={60}
                                    tabIndex={13}
                                />
                                <TextField
                                    type="text"
                                    name="address3"
                                    value={formData.address3}
                                    enableAddressFinder={true}
                                    setValue={handleValueChange}
                                    placeholder="Enter address 3"
                                    required
                                    maxChars={60}
                                    tabIndex={15}
                                />
                            </Col>

                            {/* Second Column: Start Date */}
                            <Col xs={12} md={2}>
                                <H4 className="heading-color f-w-700 mb-3 text-left">Start Date</H4>
                                <DatePicker
                                    name="startDate1"
                                    value={formData.startDate1}
                                    setValue={handleValueChange}
                                    label=""
                                    // error={formErrors.startDate1}
                                    // errorMessage="Start Date is required"
                                    // setError={handleErrorChange}
                                    tabIndex={12}
                                />
                                <DatePicker
                                    name="startDate2"
                                    value={formData.startDate2}
                                    setValue={handleValueChange}
                                    label=""
                                    tabIndex={14}
                                />
                                <DatePicker
                                    name="startDate3"
                                    value={formData.startDate3}
                                    setValue={handleValueChange}
                                    label=""
                                    tabIndex={16}
                                />
                            </Col>
                        </Row>
                    </Col>

                    <Col xs={12} className="mb-4">
                        {/* Employment Section */}
                        <H3 className="f-w-900 heading-color mt-2 text-left">Employment</H3>
                        <Col sm={12} className='sm-row mt-4' >
                            <Dropdown
                                label="Income Type"
                                name="salaryWages"
                                value={formData.salaryWages}
                                setValue={handleValueChange}
                                options={splitDropdownValues(clientFactFindData ? clientFactFindData[4].dropDownValues : '')}
                                // required={true}
                                // error={formErrors.salaryWages}
                                // setError={handleErrorChange}
                                // errorMessage="Please select income type"
                                passCol='col-3'
                                tabIndex={17}
                                design='flex'
                            />
                        </Col>

                        <section>
                            <Row>
                                <Col xs={12} md={3}>
                                    <H4 className="heading-color f-w-700 text-left">Income Type</H4>
                                </Col>
                                <Col xs={12} md={2}>
                                    <H4 className="heading-color f-w-700 text-left">Income $</H4>
                                </Col>
                                <Col xs={12} md={2}>
                                    <H4 className="heading-color f-w-700 text-left">Frequency</H4>
                                </Col>
                            </Row>

                            <Row className="align-items-center mt-2">
                                <Col xs={12} md={3}>
                                    <H5 style={{ fontSize: '14px' }} className='mb-3'>
                                        Salary / Wages
                                    </H5>
                                </Col>
                                <Col xs={12} md={2}>
                                    <DollarInput
                                        name="salaryWagesIncome"
                                        value={formData.salaryWagesIncome}
                                        setValue={handleValueChange}
                                        placeholder="Enter amount"
                                        comma={true}
                                        className="mb-0"
                                        limit={9}
                                        required
                                        tabIndex={20}
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Dropdown
                                        label=""
                                        name="salaryWagesFrequency"
                                        value={formData.salaryWagesFrequency}
                                        setValue={handleValueChange}
                                        options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
                                        required
                                        tabIndex={21}
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-center">
                                <Col xs={12} md={3} className='mb-3'>
                                    <span style={{ fontSize: '14px' }}>
                                        Family Allowance
                                    </span>
                                </Col>
                                <Col xs={12} md={2}>
                                    <DollarInput
                                        name="familyAllowance"
                                        value={formData.familyAllowance}
                                        setValue={handleValueChange}
                                        placeholder="Enter amount"
                                        comma={true}
                                        limit={9}
                                        required
                                        tabIndex={18}
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Dropdown
                                        label=''
                                        name="familyAllowanceFrequency"
                                        value={formData.familyAllowanceFrequency}
                                        setValue={handleValueChange}
                                        options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
                                        required
                                        tabIndex={19}
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-center">
                                <Col xs={12} md={3} className='mb-3'>
                                    <H5 style={{ fontSize: '14px' }}>
                                        Dividend Income
                                    </H5>
                                </Col>
                                <Col xs={12} md={2}>
                                    <DollarInput
                                        name="dividendIncome"
                                        value={formData.dividendIncome}
                                        setValue={handleValueChange}
                                        placeholder="Enter amount"
                                        comma={true}
                                        limit={9}
                                        required
                                        tabIndex={22}
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Dropdown
                                        label=""
                                        name="dividendIncomeFrequency"
                                        value={formData.dividendIncomeFrequency}
                                        setValue={handleValueChange}
                                        options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
                                        required
                                        tabIndex={23}
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-center">
                                <Col xs={12} md={3} className='mb-3'>
                                    <H5 style={{ fontSize: '14px' }}>
                                        Company Net Profit
                                    </H5>
                                </Col>
                                <Col xs={12} md={2}>
                                    <DollarInput
                                        name="companyNetProfit"
                                        value={formData.companyNetProfit}
                                        setValue={handleValueChange}
                                        placeholder="Enter amount"
                                        comma={true}
                                        limit={9}
                                        required
                                        tabIndex={24}
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Dropdown
                                        label=""
                                        name="companyNetProfitFrequency"
                                        value={formData.companyNetProfitFrequency}
                                        setValue={handleValueChange}
                                        options={splitDropdownValues(clientFactFindData ? clientFactFindData[11].dropDownValues : '')}
                                        required
                                        tabIndex={25}
                                    />
                                </Col>
                            </Row>

                        </section>
                        {/* Employment History Section */}
                        <H3 className="f-w-900 heading-color mt-4 text-left">Employment History</H3>
                        <H4 className="heading-color f-w-700 mt-2 mb-4 text-left">
                            Home Address - we need 3 years history
                        </H4>

                        <Row className="gx-2 mt-5">
                            {/* First Column: Company Name */}
                            <Col xs={12} md={6}>
                                <H4 className="heading-color f-w-700 mb-3 text-left">Employer Name</H4>
                                {[1, 2, 3].map((index) => (
                                    <TextField
                                        key={`companyName${index}`}
                                        type="text"
                                        name={`employment${index}CompanyName`}
                                        value={formData[`employment${index}CompanyName`]}
                                        setValue={handleValueChange}
                                        placeholder="Employer Name"
                                        required
                                        maxChars={60}
                                        tabIndex={index === 1 ? 26 : index === 2 ? 29 : 32}  // Specific tabIndex values: 26, 29, 32
                                    />
                                ))}
                            </Col>
                            {/* Second Column: Role */}
                            <Col xs={12} md={3}>
                                <H4 className="heading-color f-w-700 mb-3 text-left">Role</H4>
                                {[1, 2, 3].map((index) => (
                                    <TextField
                                        key={`role${index}`}
                                        type="text"
                                        name={`employment${index}Role`}
                                        value={formData[`employment${index}Role`]}
                                        setValue={handleValueChange}
                                        placeholder="Role"
                                        maxChars={20}
                                        required
                                        tabIndex={index === 1 ? 27 : index === 2 ? 30 : 33}  // Specific tabIndex values: 27, 30, 33
                                    />
                                ))}
                            </Col>

                            {/* Third Column: Start Date */}
                            <Col xs={12} md={2}>
                                <H4 className="heading-color f-w-700 mb-3 text-left">Start Date</H4>
                                {[1, 2, 3].map((index) => (
                                    <DatePicker
                                        key={`startDate${index}`}
                                        name={`employment${index}StartDate`}
                                        value={formData[`employment${index}StartDate`]}
                                        setValue={handleValueChange}
                                        label=""
                                        tabIndex={index === 1 ? 28 : index === 2 ? 31 : 34}  // Specific tabIndex values: 28, 31, 34
                                    />
                                ))}
                            </Col>
                        </Row>

                    </Col>
                    {/* Submit Button */}
                    {/* <Col xs={12} className="text-end">
                        <Button disabled={isInsertUpdateClientPending}
                            type="submit" color="success" style={{ fontSize: '18px' }}>
                            {isInsertUpdateClientPending ? "Loading . . ." : Continue}
                        </Button>
                    </Col> */}
                </Row>
            </Row>
        </Form>
    );
}
